/**
 * PlaceSearchListbox — liste deroulante a chargement progressif.
 *
 * Remplace le <ul> par defaut d'un MUI <Autocomplete> (prop
 * `ListboxComponent`) pour lui ajouter deux choses que MUI ne fournit pas :
 *
 *   1. le declenchement de la page suivante quand on approche du bas ;
 *   2. un pied de liste qui dit ou l'on en est : chargement, erreur avec
 *      « Reessayer », « 20 sur 441 », « tous les lieux sont affiches », et un
 *      bouton « Afficher plus » pour qui ne fait pas defiler (clavier, ecran
 *      assez haut pour que la premiere page ne deborde pas).
 *
 * L'etat du pied passe par un CONTEXTE, pas par des props : le composant doit
 * etre defini une seule fois, au niveau du module. Une fonction creee a chaque
 * rendu serait un nouveau type de composant pour React, qui demonterait la
 * liste — et la remettrait en haut — a chaque page chargee.
 */
import React, { createContext, forwardRef, useContext } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Skeleton from "@mui/material/Skeleton";

/** Distance au bas, en pixels, a partir de laquelle la suite est demandee. */
export const NEAR_END_PX = 96;

export interface PlaceSearchFooterState {
  shown: number;
  total: number | null;
  hasMore: boolean;
  isLoadingMore: boolean;
  hasLoadMoreError: boolean;
  onLoadMore: () => void;
  onRetry: () => void;
  labels: {
    loadingMore: string;
    loadMore: string;
    loadMoreError: string;
    retry: string;
    /** Deja interpole, ex. « 20 sur 441 lieux ». */
    count: string | null;
    /** Deja interpole, ex. « Tous les lieux sont affiches (16) ». */
    allShown: string | null;
  };
}

export const PlaceSearchFooterContext = createContext<PlaceSearchFooterState | null>(null);

/** Empeche le champ de perdre le focus — et le menu de se fermer — au clic. */
const keepFocus = (event: React.MouseEvent) => event.preventDefault();

function Footer({ state }: { state: PlaceSearchFooterState }) {
  const { labels } = state;

  if (state.isLoadingMore) {
    return (
      <Box sx={{ px: 2, py: 1 }} aria-live="polite" aria-busy="true">
        {[0, 1].map((i) => (
          <Box key={i} sx={{ display: "flex", alignItems: "center", gap: 1.25, py: 0.75 }}>
            <Skeleton variant="rounded" width={30} height={30} />
            <Box sx={{ flex: 1 }}>
              <Skeleton variant="text" width="55%" />
              <Skeleton variant="text" width="80%" sx={{ fontSize: "0.75rem" }} />
            </Box>
          </Box>
        ))}
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1, py: 0.5, color: "text.secondary", fontSize: 13 }}>
          <CircularProgress size={14} />
          {labels.loadingMore}
        </Box>
      </Box>
    );
  }

  if (state.hasLoadMoreError) {
    return (
      <Box
        role="alert"
        sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: 1, px: 2, py: 1.25, color: "error.main", fontSize: 13 }}
      >
        <span>{labels.loadMoreError}</span>
        <Button size="small" variant="outlined" color="error" onMouseDown={keepFocus} onClick={state.onRetry}>
          {labels.retry}
        </Button>
      </Box>
    );
  }

  if (state.hasMore) {
    return (
      <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: 1, px: 2, py: 1, fontSize: 13, color: "text.secondary" }}>
        <span>{labels.count}</span>
        <Button size="small" onMouseDown={keepFocus} onClick={state.onLoadMore}>
          {labels.loadMore}
        </Button>
      </Box>
    );
  }

  if (labels.allShown && state.shown > 0) {
    return (
      <Box sx={{ px: 2, py: 1, fontSize: 12, color: "text.secondary", textAlign: "center" }}>
        {labels.allShown}
      </Box>
    );
  }

  return null;
}

export const PlaceSearchListbox = forwardRef<HTMLUListElement, React.HTMLAttributes<HTMLElement>>(
  function PlaceSearchListbox({ children, onScroll, ...rest }, ref) {
    const footer = useContext(PlaceSearchFooterContext);

    const handleScroll = (event: React.UIEvent<HTMLElement>) => {
      onScroll?.(event);
      const el = event.currentTarget;
      if (footer && footer.hasMore && el.scrollHeight - el.scrollTop - el.clientHeight < NEAR_END_PX) {
        // Le hook ignore l'appel si une page est deja en vol : pas de garde ici.
        footer.onLoadMore();
      }
    };

    const footerNode = footer ? (
      // Hors des options : role="presentation" pour que les lecteurs d'ecran
      // ne l'annoncent pas comme un choix de la liste.
      <Box
        component="li"
        role="presentation"
        sx={{ listStyle: "none", position: "sticky", bottom: 0, zIndex: 1, bgcolor: "background.paper", borderTop: 1, borderColor: "divider", "&:empty": { display: "none" } }}
      >
        <Footer state={footer} />
      </Box>
    ) : null;

    /*
     * createElement plutot que JSX : l'augmentation de types de react-i18next
     * redefinit `children` des elements intrinseques et refuse un `children`
     * MUI (ReactNode) suivi d'un frere. Le rendu est strictement le meme.
     */
    return React.createElement(
      "ul",
      { ...(rest as React.HTMLAttributes<HTMLUListElement>), ref, onScroll: handleScroll },
      // Les options rendues par MUI : toujours des elements React, jamais
      // l'objet que l'augmentation de react-i18next ajoute au type.
      children as React.ReactNode,
      footerNode
    );
  }
);
